/**
 * Origen de las promociones del carrusel del checkout.
 *
 * <p><strong>Por que existe esta capa y no se llama a la API de publicidad desde el componente:</strong>
 * el carrusel vive DENTRO de una pagina de pago. Meter un origen de terceros ahi tiene tres costes que
 * no son teoricos: obliga a abrir `connect-src`/`img-src` en la CSP, mete a ese tercero en el alcance
 * PCI DSS SAQ A del proyecto (CONVENCIONES-CODIGO 3.3), y convierte la disponibilidad del checkout en
 * rehen de un servidor de anuncios. Aislando el origen detras de {@link PromoSource}, cambiar de donde
 * salen las promos no toca el componente ni la pantalla de pago.
 *
 * <p>Hoy la fuente son imagenes EMPAQUETADAS (ver {@link bundledPromoSource}): cero red, cero CSP, cero
 * terceros. Estan pensadas para la demo.
 *
 * <p>Cuando se enchufe la API de publicidad que ya existe, la recomendacion es que la llame el
 * backend y que el widget siga pidiendo a NUESTRO origen: asi se puede cachear, poner un timeout
 * corto y validar las URL de imagen igual que ya se valida `logo_url`. Lo unico que se implementa
 * aqui es la frontera; la decision queda abierta a proposito.
 */

/** Una promocion renderizable. El carrusel no sabe de donde salio. */
export interface Promo {
  /** Identificador estable; se usa como key de React y para los puntos de navegacion. */
  readonly id: string;
  /**
   * Texto alternativo. OBLIGATORIO y no decorativo: el carrusel es contenido, no adorno, y un
   * lector de pantalla tiene que poder saltarlo sabiendo que se salta.
   */
  readonly alt: string;
  /** Contenido visual. Ver {@link BundledPromoArt} para la variante empaquetada. */
  readonly art: BundledPromoArt;
  /**
   * Destino opcional. Se abre SIEMPRE en una pestana nueva y con `rel="noopener noreferrer"`: una
   * promocion no puede navegar la pagina del pago ni obtener referencia a esta ventana.
   */
  readonly href?: string | undefined;
}

/**
 * Ilustracion empaquetada, identificada por nombre. NO es una URL ni HTML: el componente traduce el
 * nombre a un SVG que vive en el bundle.
 *
 * <p>Es deliberado que sea un enum cerrado y no una cadena libre. Si esto fuese `string` con una URL,
 * el dia que las promos vengan de una API una respuesta comprometida podria apuntar a cualquier
 * origen, o colar un `javascript:`; con un nombre cerrado, lo peor que puede pasar es que no
 * corresponda a ninguna ilustracion y no se pinte nada.
 */
export type BundledPromoArt = 'envio' | 'cuotas' | 'devolucion';

/** De donde salen las promos de una sesion de checkout. */
export interface PromoSource {
  /**
   * Promos a mostrar. Devolver una lista vacia es una respuesta valida y significa "no hay
   * publicidad": el carrusel no se pinta y el pago no se entera.
   */
  promos(): readonly Promo[];
}

/**
 * Promos de relleno para la demo. Genericas a proposito: no nombran comercios, no prometen precios y
 * no llevan enlace, porque un enlace de relleno que no lleva a ningun sitio es peor que ninguno.
 */
const PLACEHOLDER_PROMOS: readonly Promo[] = [
  {
    id: 'promo-envio',
    alt: 'Promoción: envío sin costo en compras sobre $30.000.',
    art: 'envio',
  },
  {
    id: 'promo-cuotas',
    alt: 'Promoción: hasta 12 cuotas sin interés con tarjetas participantes.',
    art: 'cuotas',
  },
  {
    id: 'promo-devolucion',
    alt: 'Promoción: 30 días para devolver sin preguntas.',
    art: 'devolucion',
  },
];

/**
 * Fuente empaquetada. Es la que se usa en la demo y la que sirve de red de seguridad: si la fuente
 * real falla, el checkout puede caer a esta sin quedarse con un hueco vacio.
 */
export const bundledPromoSource: PromoSource = {
  promos: () => PLACEHOLDER_PROMOS,
};
